import { useEffect, useState, FC } from "react";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";
import { ScrollBar } from "@ht/components";
import MessageHeader from "./MessageHeader";
import MessageBody from "./MessageBody";
import MessageFooter from "./MessageFooter";
import { IActiveRoom, IIncomingRoom, IMessage, IStatus } from "../../../app/chatAgent/types";
import { useFetchChatAgentHistory } from "../hooks/useFetchChatAgentHistory";
import { useHttpRequest } from "../../../hooks/useHttpRequest";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import * as chatActions from "../../../app/chatAgent/chatAgentSlice";
import { readAgentMessage } from "../../../app/chatAgent/chatAgentSlice";
import dayjs from "dayjs";
import { useFetchAgentRoom } from "../hooks/useFetchAgentRoom";

interface IProps {
    className?: string;
}
const MessageBar: FC<IProps> = ({ className }) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { activeRoom, scrollPosition, status: chatAgentCallback, incomingRoom, messages, layoutType, floatingRoomId } = useAppSelector((state) => state.chatagent);
    const [isScroll, setIsScroll] = useState(false);
    const [bodyOpen, setBodyOpen] = useState(false);
    const [roomId, setRoomId] = useState("");
    const [limit] = useState(20);
    const { chatId } = useParams();
    const { sendRequest } = useHttpRequest();
    const navigate = useNavigate();
    const [opponent, setOpponent] = useState<IIncomingRoom>();
    const [chatList, setChatList] = useState<IMessage[]>();

    const { retrieveData, chatBaseUrl, chatHistoryUrl } = useFetchChatAgentHistory(activeRoom.sessionId, limit, user?.username ?? "_");

    const onScrollUpHandler = async (e: HTMLElement) => {
        const prevHeight = e.scrollHeight;
        if (e.scrollTop == 0) {
            const room = incomingRoom.find(a => a.sessionId == activeRoom.sessionId);
            if (room) {
                const offset = (isNaN(room.offset) ? 0 : room.offset) + limit;
                const response = await sendRequest({
                    baseUrl: chatBaseUrl,
                    url: `${chatHistoryUrl}&offset=${offset}`,
                    method: "get"
                });
                if (response.data.length > 0) {
                    dispatch(chatActions.updateChatAgentRoomOffset({
                        offset,
                        sessionId: room.sessionId
                    }));
                }
                retrieveData(response);
                setTimeout(() => {
                    e.scrollTop = e.scrollHeight - prevHeight;
                }, 50);
                dispatch(chatActions.setChatAgentScrollPosition("stay"));
            }
        }
    };

    const changeRoom = () => {
        if (roomId) {
            setBodyOpen(true);
            const currentActiveRoom = incomingRoom.find(x => x.roomId === roomId);
            if (currentActiveRoom) {
                dispatch(chatActions.activeChatAgentRoom(currentActiveRoom));
            }

            if (scrollPosition == "bottom") setIsScroll((prev) => !prev);
            dispatch(readAgentMessage({ sessionId: currentActiveRoom?.sessionId, messageId: 0 }));
            setOpponent(incomingRoom.find(a => a.roomId == roomId));
            dispatch(chatActions.setFloatingOpponent(incomingRoom.find(a => a.roomId == roomId)))
        }
    }

    useEffect(() => {
        if (layoutType == "PAGE" && chatId)
            setRoomId(chatId);
        else {
            setRoomId(activeRoom?.roomId);
        }

        if (layoutType == "FLOATING_PANEL") {
            setRoomId(floatingRoomId);
        }
    }, [chatId, activeRoom, floatingRoomId, layoutType]);

    useEffect(() => {
        changeRoom();
    }, [roomId, incomingRoom]);

    useFetchAgentRoom(String(user?.username));

    useEffect(() => {
        if (messages && opponent) {
            const filteredMessages = messages.filter(a => a.sessionId == opponent.sessionId);
            const sortedMessages = filteredMessages.sort((a, b) =>
                new Date(dayjs(a.messageTime).format('YYYY-MM-DD HH:mm:ss.SSS')).getTime() -
                new Date(dayjs(b.messageTime).format('YYYY-MM-DD HH:mm:ss.SSS')).getTime());
            setChatList(sortedMessages);
        }
    }, [messages, opponent]);

    useEffect(() => {
        if (scrollPosition == "bottom") setIsScroll((prev) => !prev);
    }, [scrollPosition, chatList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!opponent) {
                localStorage.removeItem('activeChatRoom');
                if (layoutType == "PAGE") navigate('/chat');
                dispatch(chatActions.activeChatAgentRoom({} as IActiveRoom));
            }

        }, 1000);
        return () => clearTimeout(timer);
    }, [opponent,layoutType]);

    useEffect(() => {
        if (chatAgentCallback) {
            if (chatAgentCallback.status == "ONLINE") {
                dispatch(chatActions.chatAgentCallBack({} as IStatus));
            }
        }
    }, [chatAgentCallback]);

    return (
        <div
            className={clsx(
                "h-[calc(100vh_-_55px)]",
                "main-content rounded-md bg-gray-50 maxLg:absolute maxLg:w-full maxLg:transition-transform flex-auto",
                !bodyOpen &&
                "maxLg:invisible maxLg:translate-x-[calc(100%_+_150px)]",
                bodyOpen && "maxLg:visible maxLg:translate-x-0 border-r border-gray-100",
                className
            )}
        >
            {opponent &&
                <div className={clsx("h-[100%]")}>
                    <div className={clsx("h-[100%]")}>
                        {layoutType !== "FLOATING_PANEL" &&
                            <MessageHeader
                                name={opponent.fullName}
                                status={opponent.status}
                            />
                        }

                        <ScrollBar
                            initialView="bottom"
                            bottom={layoutType != "FLOATING_PANEL" ? "120px" : "60px"}
                            onScrollUp={onScrollUpHandler}
                            isPositionChange={isScroll}>
                            <MessageBody chats={chatList} />
                        </ScrollBar>
                        <MessageFooter roomId={opponent.roomId} />
                    </div>
                </div>
            }
        </div>
    );
};

export default MessageBar;
